import {useId,useLayoutEffect,useRef,type CSSProperties,type HTMLAttributes,type ReactNode} from 'react';
import './SteppedNumberInput.css';

// Up to ten readable intervals; small ranges naturally use single-unit stops.
function automaticStops(minimum:number,maximum:number){
 if(maximum<=minimum)return [minimum];
 const rough=(maximum-minimum)/10,power=10**Math.floor(Math.log10(rough));
 const step=Math.max(1,[1,2,5,10].find(n=>n*power>=rough)!*power);
 const stops=[minimum];
 for(let n=(Math.floor(minimum/step)+1)*step;n<maximum;n+=step)stops.push(n);
 return [...stops,maximum];
}

export function SteppedNumberInput({label,text,value,minimum,maximum,stops:presets,onChange,disabled=false,invalid=false,describedBy,unit,inputMode='numeric',formatValue=String,className='',layout='inline',boundLabels,children}:{
 label:string;text:string;value:number;minimum:number;maximum:number;stops?:number[];onChange:(text:string)=>void;disabled?:boolean;invalid?:boolean;describedBy?:string;unit?:string;inputMode?:HTMLAttributes<HTMLInputElement>['inputMode'];formatValue?:(value:number)=>string;className?:string;layout?:'inline'|'stacked';boundLabels?:{minimum:string;maximum:string};children?:ReactNode;
}){
 const id=useId(),scale=useRef<HTMLDivElement>(null),stops=presets??automaticStops(minimum,maximum);
 const clamped=Math.min(maximum,Math.max(minimum,value));
 // Counts use a numeric scale. Explicit price presets retain their equal slots.
 const positionOf=(n:number)=>{
  if(maximum===minimum)return 0;
  if(!presets)return (n-minimum)/(maximum-minimum);
  const upper=stops.findIndex(stop=>stop>=n);
  return upper<=0?0:(upper-1+(n-stops[upper-1])/(stops[upper]-stops[upper-1]))/(stops.length-1);
 };
 const position=positionOf(clamped);
 // Measure the printed labels, not their larger button targets. Keep every
 // selectable stop visible; crowding uses a second row instead of hiding it.
 // Currency already appears in the bound labels; repeating it on the narrow
 // scale would push an adjacent selectable preset out of view.
 const scaleLabel=(n:number,i:number)=>!boundLabels&&(i===0||i===stops.length-1)?formatValue(n):n;
 const scaleKey=stops.map((n,i)=>[positionOf(n),scaleLabel(n,i)].join(':')).join('|');
 useLayoutEffect(()=>{
  const element=scale.current;if(!element)return;
  let active=true,frame=0,lastWidth=-1;
  const fit=()=>{
   if(!active)return;
   const labels=Array.from(element.children) as HTMLElement[];
   for(const label of labels){label.style.visibility='visible';label.style.top='0px';}
   if(labels.length<2){element.style.height=labels[0]?.getBoundingClientRect().height+'px';return;}
   const rowHeight=labels[0].getBoundingClientRect().height;
   const bounds=(label:HTMLElement)=>label.firstElementChild!.getBoundingClientRect();
   const right=[bounds(labels[0]).right],end=bounds(labels.at(-1)!).left;
   labels.slice(1,-1).forEach(label=>{
    const box=bounds(label);
    let row=0;
    while(box.left<(right[row]??-Infinity)+4||row===0&&box.right>end-4)row++;
    label.style.top=row*rowHeight+'px';right[row]=box.right;
   });
   element.style.height=right.length*rowHeight+'px';
  };
  // Fitting changes the scale's own height. Observe width changes and defer
  // that write outside ResizeObserver delivery, including newly shown panels.
  const queueFit=()=>{if(active&&!frame)frame=requestAnimationFrame(()=>{frame=0;fit();});};
  fit();const observer=new ResizeObserver(entries=>{const width=entries[0]?.contentRect.width;if(width!==undefined&&width!==lastWidth){lastWidth=width;queueFit();}});observer.observe(element);
  void document.fonts.ready.then(queueFit);
  return()=>{active=false;cancelAnimationFrame(frame);observer.disconnect();};
 },[scaleKey,value]);
 const edit=(next:string)=>{
  if(next===''){onChange('');return;}
  if(!(inputMode==='decimal'?/^\d+(?:[.,]\d{0,2})?$/:/^\d+$/).test(next))return;
  onChange(Number(next.replace(',','.'))>maximum?String(maximum):next.replace(/^0+(?=\d)/,''));
 };
 return <div className={'stepped-number-input '+className} data-layout={layout}>
  <div className="stepped-number-slider">
   {boundLabels?<div className="stepped-number-bounds"><span><small>{boundLabels.minimum}</small><strong>{formatValue(minimum)}</strong></span><span><small>{boundLabels.maximum}</small><strong>{formatValue(maximum)}</strong></span></div>:<label htmlFor={id}>{label}</label>}
   <div className="stepped-number-track" data-disabled={disabled||stops.length<2}><input id={id} type="range" min={0} max={1} step="any" value={position} style={{'--range-progress':position*100+'%'} as CSSProperties} aria-label={boundLabels?label:undefined} aria-valuemin={minimum} aria-valuemax={maximum} aria-valuenow={clamped} aria-valuetext={formatValue(clamped)} aria-describedby={describedBy} disabled={disabled||stops.length<2} onChange={e=>{
    const next=Number(e.target.value),stop=stops.reduce((best,n)=>Math.abs(positionOf(n)-next)<Math.abs(positionOf(best)-next)?n:best);
    e.currentTarget.value=String(positionOf(stop));onChange(String(stop));
   }} onKeyDown={e=>{
    if(!['Home','End','ArrowLeft','ArrowDown','ArrowRight','ArrowUp'].includes(e.key))return;
    e.preventDefault();onChange(String(e.key==='Home'?minimum:e.key==='End'?maximum:['ArrowLeft','ArrowDown'].includes(e.key)?stops.findLast(n=>n<value)??minimum:stops.find(n=>n>value)??maximum));
   }}/></div>
   <div ref={scale} className="stepped-number-scale" role="group" aria-label={label}>{stops.map((n,i)=><button type="button" key={n} data-active={n===value} data-stop={n} data-bound={i===0?'minimum':i===stops.length-1?'maximum':undefined} style={{left:positionOf(n)*100+'%'}} aria-label={`${label}: ${formatValue(n)}`} aria-pressed={n===value} disabled={disabled||stops.length<2} onClick={()=>onChange(String(n))}><span>{scaleLabel(n,i)}</span></button>)}</div>
  </div>
  <div className="stepped-number-details">
   {children}
   <div className="stepped-number-entry"><input id={id+'-entry'} className="form-input" style={{width:`calc(${Math.max(3,String(Math.ceil(maximum)).length)+(inputMode==='decimal'?3:0)}ch + 24px)`}} aria-label={label} inputMode={inputMode} aria-invalid={invalid} aria-describedby={describedBy} value={text} disabled={disabled} onChange={e=>edit(e.target.value)} onBlur={()=>onChange(String(Math.min(maximum,Math.max(minimum,Number(text.replace(',','.'))||minimum))))}/>{unit&&<span>{unit}</span>}</div>
  </div>
 </div>;
}
